"use client"

import clsx from "clsx"
import { useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { safeFilesFromEvent } from "../../utils/safeFilesFromEvent"
import { TIconButton } from "../../ui/button/TIconButton"
import { TIcon } from "../../ui/display/TIcon"
import { TText } from "../../ui/display/TText"
import { CursorFollow, CursorProvider } from "../../ui/shadcn-io/animated-cursor"
import { ImageUploadMenu } from "./image-upload-menu"

interface ReferenceImagesUploadProps {
	value: string[]
	onChange: (value: string[]) => void
	onWorkHistorySelect?: () => Promise<string[]>
}

export function ReferenceImagesUpload({ value, onChange, onWorkHistorySelect }: ReferenceImagesUploadProps) {
	const images = value || []

	const handleRemove = useCallback(
		(index: number) => {
			const newImages = images.filter((_, i) => i !== index)
			onChange(newImages)
		},
		[images, onChange]
	)

	const handleWorkHistorySelect = useCallback(async () => {
		if (!onWorkHistorySelect) return

		try {
			const selectedImages = await onWorkHistorySelect()
			if (selectedImages.length > 0) {
				const availableSlots = 6 - images.length
				const imagesToAdd = selectedImages.slice(0, availableSlots)
				const updatedImages = [...images, ...imagesToAdd].slice(0, 6)
				onChange(updatedImages)
			}
		} catch (error) {
			console.error("Failed to select images from work history:", error)
		}
	}, [onWorkHistorySelect, images, onChange])

	const handleFiles = useCallback(
		(files: File[]) => {
			const validFiles = files.filter((file) => file.type.startsWith("image/"))
			if (validFiles.length === 0) return

			let processedCount = 0
			const newImages: string[] = []

			validFiles.forEach((file) => {
				const reader = new FileReader()
				reader.onload = (e) => {
					const result = e.target?.result as string
					newImages.push(result)
					processedCount++

					if (processedCount === validFiles.length) {
						const updatedImages = [...images, ...newImages].slice(0, 6)
						onChange(updatedImages)
					}
				}
				reader.readAsDataURL(file)
			})
		},
		[images, onChange]
	)

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (acceptedFiles.length > 0) {
				const availableSlots = 6 - images.length
				const filesToProcess = acceptedFiles.slice(0, availableSlots)
				handleFiles(filesToProcess)
			}
		},
		[handleFiles, images.length]
	)

	const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
		// 임베디드 웹뷰의 FS 핸들 거부(NotAllowedError) 드롭 폴백 — safeFilesFromEvent 주석 참조.
		getFilesFromEvent: safeFilesFromEvent,
		onDrop,
		accept: {
			"image/*": [".png", ".jpg", ".jpeg", ".webp", ".gif"]
		},
		disabled: images.length === 6,
		noClick: true,
		noDrag: images.length === 6,
		multiple: true
	})

	return (
		<div {...getRootProps()} className="relative flex flex-col gap-4 w-full bg-g800/10 rounded-lg p-4">
			<input {...getInputProps()} />
			<div className="flex items-center justify-between gap-2">
				<TText variant="caption" color="secondary" bold>
					Reference
				</TText>
				<TText color="tertiary">{images.length}/6</TText>
			</div>
			<div className="grid grid-cols-3 gap-3">
				{images.map((image, index) => (
					<div key={index} className="relative group rounded-lg overflow-hidden aspect-square bg-g0">
						<img src={image} alt={`Reference ${index !== undefined ? index + 1 : ""}`} className="w-full h-full object-cover" />
						<div className="absolute inset-0 bg-g0/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
							<TIconButton name="close" size="xsmall" color="tertiary" className="absolute top-1 right-1" onClick={() => handleRemove(index)} />
						</div>
					</div>
				))}
				{images.length < 6 && (
					<ImageUploadMenu onDeviceUpload={open} onWorkHistoryUpload={onWorkHistorySelect ? handleWorkHistorySelect : undefined}>
						{({ open }) => (
							<div className="relative group w-full aspect-square flex items-center justify-center cursor-pointer">
								<TIcon name="add" size={24} color="text-tertiary" />
								<div className="absolute inset-0 border-2 border-dashed border-g800/20 rounded-lg overflow-hidden" />
								<div
									className={clsx(
										"absolute inset-0 bg-g800/20 rounded-lg overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-300",
										open && "opacity-100"
									)}
								/>
								<FollowingCursor />
							</div>
						)}
					</ImageUploadMenu>
				)}
			</div>
			{isDragActive && (
				<div className="absolute inset-0 flex items-center justify-center border border-dashed border-primary rounded-lg backdrop-blur-[40px] shadow-[0_2_8_0_rgba(0,0,0,0.14)] z-50 bg-g250/60">
					<TText color="primary">Drop files here</TText>
				</div>
			)}
		</div>
	)
}

function FollowingCursor() {
	return (
		<CursorProvider>
			<CursorFollow sideOffset={0}>
				<div className="px-4 py-2 rounded-full text-sm shadow-lg bg-g250/60 border border-g800/20 whitespace-nowrap">Upload or drag your image</div>
			</CursorFollow>
		</CursorProvider>
	)
}
